// Green Screen Yapper Ad's draft as the form keeps it (spec G14-green-screen-yapper, PR h): the
// server holds the member's draft (`.../templates/green-screen-yapper-ad/draft`), so it survives
// the panel; the form reads it, keeps its edits there, and asks the server's script director, the
// stills and the card check (live's `useGreenScreenDraft`, which ran them from the browser). As
// Pixar Talking Ad's (`pixar-draft.ts`): one approval before the images, then the video. The last
// "Generate final video" is read back on the draft (`run`): a run that ended without its video
// keeps its unchanged clips' takes for the next, and Revise opens the clips its video model
// refused (live's `useGreenScreenVideoRuns`, PR f4).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetGreenScreenAdDraftQueryKey,
  useApproveGreenScreenAdRevision,
  useApproveGreenScreenAdScript,
  useBackToGreenScreenAdScript,
  useGetGreenScreenAdDraft,
  useRegenerateGreenScreenAdStill,
  useResetGreenScreenAdDraft,
  useReviseGreenScreenAdClips,
  useWriteGreenScreenAdDraft,
  useWriteGreenScreenAdScript,
  writeGreenScreenAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  GreenScreenDraft,
  GreenScreenDraftRun,
  GreenScreenDraftStill,
  GreenScreenRunClip,
  GreenScreenScript,
  GreenScreenStillName,
  GreenScreenStills,
  GreenScreenTakes,
  GreenScreenVideoInputs,
  OutputRef,
} from "../../../api/generated/model";
import {
  GreenScreenApproval,
  GreenScreenDraftWrite,
  GreenScreenInputs,
  GreenScreenScriptAsk,
  type GreenScreenDraftWriteOutput,
  type GreenScreenInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface GreenScreenForm {
  inputs: GreenScreenInputsOutput;
  script: GreenScreenScript | null;
}

/** Approval 2's stills in live's order (`STILL_ORDER`), each with live's caption
 * (`STILL_LABELS`). */
export const STILL_ORDER: readonly GreenScreenStillName[] = ["creator", "pose_2", "pose_3", "pose_4", "card"];
export const STILL_LABELS: Readonly<Record<GreenScreenStillName, string>> = {
  creator: "Creator · clip 1 starts here; the poses are made from it",
  pose_2: "Clip 2 start · counting one",
  pose_3: "Clip 3 start · four fingers",
  pose_4: "Clip 4 start · pointing",
  card: "Card · on screen the whole ad",
};

/** What each still is drawn from (live's `STILL_GRAPH.sources`): the poses from the creator. */
const STILL_SOURCES: Readonly<Partial<Record<GreenScreenStillName, GreenScreenStillName>>> = {
  pose_2: "creator",
  pose_3: "creator",
  pose_4: "creator",
};

/** Live's words (`library-view/constants.ts`). */
export const REFUSED_FRAME_MESSAGE = "The video model refused the clip of this frame — regenerate the frame.";
export const REFUSED_CLIP_MESSAGE =
  "The video model refused the clip of this frame — rephrase its lines (Back to script) or regenerate the frame.";

/** A run's talking clips by letter: the script's clip and the still it starts on. */
const TALKING: Readonly<Record<Exclude<GreenScreenRunClip, "card">, { clip: number; still: GreenScreenStillName }>> = {
  a: { clip: 1, still: "creator" },
  b: { clip: 2, still: "pose_2" },
  c: { clip: 3, still: "pose_3" },
  d: { clip: 4, still: "pose_4" },
};
const LETTERS = ["a", "b", "c", "d"] as const;
/** A clip's takes in the order a plan runs them: `take_count` of these. */
const TAKE_WORDS = ["first", "second", "third"] as const;

/** The draft refetched while it works: every 2 s while the director or a still works, and a
 * moment after (a step's end moves the draft a moment after its job ends); every 10 s while its
 * last run runs, for its takes and its refused clips once it ends (a run takes minutes). */
const POLL_MS = 2_000;
const RUN_POLL_MS = 10_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** A still under way (live's `isStillWaiting`): drawing, or pending on a still that is done or
 * still coming; the card while a card is drawn or read back. */
export function stillWaiting(name: GreenScreenStillName, stills: readonly GreenScreenDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const source = STILL_SOURCES[name];
  if (source === undefined) return false;
  return stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills);
}

/** Where the draft's work stands, as live's progress line names it. */
export interface DraftWork {
  writing: boolean;
  drawing: boolean;
  checking: boolean;
}

export function workOf(draft: GreenScreenDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    writing: draft.script_director === "working" || answering,
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills)),
    checking: draft.card_checking,
  };
}

function working(work: DraftWork): boolean {
  return work.writing || work.drawing || work.checking;
}

/** The stills the draft drew, by name, once every one is done and the card kept; null before. */
export function drawnStills(draft: GreenScreenDraft): GreenScreenStills | null {
  if (draft.stage !== "cast" || draft.card_check === null) return null;
  const drawn = Object.fromEntries(
    draft.stills.flatMap((each) => (each.status === "done" && each.output ? [[each.name, each.output] as const] : [])),
  );
  const { creator, pose_2: pose2, pose_3: pose3, pose_4: pose4, card } = drawn;
  if (creator === undefined || pose2 === undefined || pose3 === undefined || pose4 === undefined || card === undefined) {
    return null;
  }
  return { creator, pose_2: pose2, pose_3: pose3, pose_4: pose4, card };
}

/** The last run, once it ended without its video (live's paused run: some clip had no take), whose
 * takes the next run keeps; null while it runs, after it delivered, and before any. */
export function pausedRun(draft: GreenScreenDraft): GreenScreenDraftRun | null {
  const run = draft.run ?? null;
  return run !== null && run.ended && run.error_code !== null ? run : null;
}

/** Revise: the script open again with the approved images kept. */
export function revising(draft: GreenScreenDraft): boolean {
  return draft.stage === "script" && draft.stills.length > 0;
}

/** The talking clips the paused run's video model refused (live's `refusedClips`), by number. */
export function refusedClips(draft: GreenScreenDraft): number[] {
  const run = pausedRun(draft);
  if (run === null) return [];
  return run.refused.flatMap((clip) => (clip === "card" ? [] : [TALKING[clip].clip]));
}

/** The clips open while revising (live's `revisedClips`); none otherwise. */
export function revisedClips(draft: GreenScreenDraft): number[] {
  return revising(draft) ? refusedClips(draft) : [];
}

function sameOutput(one: OutputRef | null | undefined, other: OutputRef | null | undefined): boolean {
  return one?.job_id === other?.job_id && one?.output_index === other?.output_index;
}

/** Live's refused-clip notice on each still a refused clip started on, while it is still that
 * still (a still drawn again since is a new clip): the card's says to draw the frame again. */
export function refusedFlags(draft: GreenScreenDraft): Readonly<Partial<Record<GreenScreenStillName, string>>> {
  const run = pausedRun(draft);
  const flags: Partial<Record<GreenScreenStillName, string>> = {};
  if (run === null) return flags;
  for (const clip of run.refused) {
    const still = clip === "card" ? "card" : TALKING[clip].still;
    const now = draft.stills.find((each) => each.name === still)?.output;
    if (sameOutput(now, run.inputs.stills[still])) {
      flags[still] = clip === "card" ? REFUSED_FRAME_MESSAGE : REFUSED_CLIP_MESSAGE;
    }
  }
  return flags;
}

function sameJson(one: unknown, other: unknown): boolean {
  return JSON.stringify(one) === JSON.stringify(other);
}

/**
 * Live's `toCurrentTake`: the paused run's takes of each clip whose request the next run leaves
 * as it was (its still, the clip model, the resolution, the route, the creator and, for a
 * talking clip, its lines and its beat); the next run keeps them as they are (`takes`,
 * Engine-8) and makes the rest. Only the takes the next plan has are named, and a clip only if one
 * remains: live keeps one take a clip, whatever the count, and a take past `take_count` is refused.
 */
export function currentTakes(draft: GreenScreenDraft, next: GreenScreenVideoInputs): GreenScreenTakes {
  const run = pausedRun(draft);
  const takes: GreenScreenTakes = {};
  if (run === null) return takes;
  const was = run.inputs;
  const settings = (inputs: GreenScreenVideoInputs) => [
    inputs.video_model,
    inputs.resolution,
    inputs.composite_route,
    inputs.photo_creator,
  ];
  if (!sameJson(settings(was), settings(next))) return takes;
  const card = run.delivered.card ?? null;
  if (card !== null && sameOutput(was.stills.card, next.stills.card)) takes.card = card;
  if (!sameJson(was.script.creator, next.script.creator)) return takes;
  const planned: readonly string[] = TAKE_WORDS.slice(0, next.take_count ?? 1);
  for (const letter of LETTERS) {
    const { clip, still } = TALKING[letter];
    const delivered = (run.delivered[letter] ?? []).filter((each) => planned.includes(each.take));
    const of = (script: GreenScreenScript) => [
      script.lines.filter((line) => line.clip === clip),
      script.beats.filter((beat) => beat.clip === clip),
    ];
    if (delivered.length > 0 && sameOutput(was.stills[still], next.stills[still]) && sameJson(of(was.script), of(next.script))) {
      takes[letter] = delivered;
    }
  }
  return takes;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: GreenScreenDraft): GreenScreenForm {
  const inputs = GreenScreenInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : GreenScreenInputs.parse({}),
    script: draft.script,
  };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function useGreenScreenDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetGreenScreenAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<GreenScreenForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetGreenScreenAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const running = data?.run !== undefined && data.run !== null && !data.run.ended;
        const poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : running ? RUN_POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: GreenScreenDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteGreenScreenAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveGreenScreenAdScript(asks);
  const regeneration = useRegenerateGreenScreenAdStill(asks);
  const back = useBackToGreenScreenAdScript(asks);
  const revise = useReviseGreenScreenAdClips(asks);
  const revision = useApproveGreenScreenAdRevision(asks);
  const reset = useResetGreenScreenAdDraft(asks);
  const asking =
    script.isPending ||
    approval.isPending ||
    regeneration.isPending ||
    back.isPending ||
    revise.isPending ||
    revision.isPending ||
    reset.isPending;
  const work: DraftWork =
    draft === undefined ? { writing: false, drawing: false, checking: false } : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteGreenScreenAdDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: GreenScreenDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writeGreenScreenAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = GreenScreenDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    /** The draft read again now. */
    refresh: refetch,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending || revision.isPending,
    resetting: reset.isPending,
    edit(change: Partial<GreenScreenForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<GreenScreenInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = GreenScreenScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = GreenScreenApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: GreenScreenStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    backToScript() {
      ask();
      back.mutate({ teamId, brandId });
    },
    /** Live's Revise: the refused clips' lines open again, the images kept. */
    reviseClips() {
      ask();
      revise.mutate({ teamId, brandId });
    },
    /** Live's "Approve and generate final video" while revising: the revision approved, then
     * `onApproved` starts the run with the draft as answered. */
    approveRevision(onApproved: (draft: GreenScreenDraft) => void) {
      if (form === null) return;
      const body = GreenScreenApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      revision.mutate({ teamId, brandId, data: body.data }, { onSuccess: onApproved });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
